
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import AppointmentActionButtons from './AppointmentActionButtons';
import CancellationModal from './CancellationModal';
import RescheduleModal from './RescheduleModal';
import AppointmentManagementService from '@/services/AppointmentManagementService';
import { Appointment, DateTimeSlot } from '@/types/dashboard';

interface AppointmentManagementProps {
  appointment: Appointment;
  userRole: 'psychologist' | 'patient';
  onAppointmentUpdated?: (updatedAppointment: Appointment) => void;
}

/**
 * Componente para testar e validar o fluxo de gerenciamento de consultas
 * Integra os botões de ação, modais e serviços
 */
const AppointmentManagement: React.FC<AppointmentManagementProps> = ({
  appointment,
  userRole,
  onAppointmentUpdated
}) => {
  // Estados para controlar a exibição dos modais
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<DateTimeSlot[]>([]);
  
  // Efeito para buscar políticas quando o componente é montado
  const [cancellationPolicy, setCancellationPolicy] = useState<any>(null);
  const [reschedulePolicy, setReschedulePolicy] = useState<any>(null);

  useEffect(() => {
    if (appointment?.id) {
      // Buscar políticas de cancelamento e reagendamento
      const fetchPolicies = async () => {
        try {
          const cancelPolicy = await AppointmentManagementService.getCancellationPolicy(appointment.id);
          const reschedPolicy = await AppointmentManagementService.getReschedulePolicy(appointment.id);
          
          setCancellationPolicy(cancelPolicy);
          setReschedulePolicy(reschedPolicy);
        } catch (error) {
          console.error('Erro ao buscar políticas:', error);
          toast.error('Não foi possível carregar as políticas de cancelamento e reagendamento.');
        }
      };
      
      fetchPolicies();
    }
  }, [appointment?.id]);

  // Função para buscar horários disponíveis para reagendamento
  const fetchAvailableSlots = async (date: Date) => {
    if (!appointment?.psychologistId) return;
    
    try {
      // Define um período de 1 dia para buscar horários disponíveis
      const endDate = new Date(date);
      endDate.setDate(endDate.getDate() + 1);
      
      const slots = await AppointmentManagementService.getAvailableSlots(
        appointment.psychologistId,
        date,
        endDate
      );
      
      setAvailableSlots(slots);
    } catch (error) {
      console.error('Erro ao buscar horários disponíveis:', error);
      toast.error('Não foi possível carregar os horários disponíveis.');
    }
  };

  // Handlers para ações de consulta
  const handleCancelAppointment = (appointmentId: string) => {
    setShowCancellationModal(true);
  };

  const handleRescheduleAppointment = (appointmentId: string) => {
    setShowRescheduleModal(true);
  };

  const handleViewDetails = (appointmentId: string) => {
    // Implementação futura - exibir detalhes da consulta
    toast.success(`Visualizando detalhes da consulta ${appointmentId}`);
  };

  // Handler para confirmar cancelamento
  const handleConfirmCancellation = async (appointmentId: string, reason: string) => {
    setIsProcessing(true);
    
    try {
      const result = await AppointmentManagementService.cancelAppointment(
        appointmentId,
        reason,
        userRole
      );
      
      toast.success('A consulta foi cancelada com sucesso.');
      
      // Atualizar o estado da consulta no componente pai, se fornecido
      if (onAppointmentUpdated) {
        const updatedAppointment = {
          ...appointment,
          status: 'cancelled' as const,
          cancellationReason: reason,
          cancelledBy: userRole,
          cancelledAt: new Date()
        };
        onAppointmentUpdated(updatedAppointment);
      }
      
      return result;
    } catch (error) {
      console.error('Erro ao cancelar consulta:', error);
      toast.error('Não foi possível cancelar a consulta. Tente novamente.');
      throw error;
    } finally {
      setIsProcessing(false);
      setShowCancellationModal(false);
    }
  };

  // Handler para confirmar reagendamento
  const handleConfirmReschedule = async (appointmentId: string, reason: string, newDateTime: Date) => {
    setIsProcessing(true);
    
    try {
      const result = await AppointmentManagementService.createRescheduleRequest(
        appointmentId,
        reason,
        newDateTime,
        userRole
      );
      
      toast.success(userRole === 'psychologist' 
        ? 'O paciente receberá uma notificação para confirmar o novo horário.'
        : 'O psicólogo receberá uma notificação para confirmar o novo horário.');
      
      // Atualizar o estado da consulta no componente pai, se fornecido
      if (onAppointmentUpdated) {
        const updatedAppointment = {
          ...appointment,
          status: 'pending_reschedule' as const
        };
        onAppointmentUpdated(updatedAppointment);
      }
      
      return result;
    } catch (error) {
      console.error('Erro ao solicitar reagendamento:', error);
      toast.error('Não foi possível solicitar o reagendamento. Tente novamente.');
      throw error;
    } finally {
      setIsProcessing(false);
      setShowRescheduleModal(false);
    }
  };

  return (
    <div>
      {/* Botões de ação para a consulta */}
      <AppointmentActionButtons
        appointment={appointment}
        userRole={userRole}
        onCancel={handleCancelAppointment}
        onReschedule={handleRescheduleAppointment}
        onViewDetails={handleViewDetails}
        disabled={isProcessing}
      />
      
      {/* Modal de cancelamento */}
      {cancellationPolicy && (
        <CancellationModal
          appointment={appointment}
          userRole={userRole}
          isOpen={showCancellationModal}
          onClose={() => setShowCancellationModal(false)}
          onConfirm={handleConfirmCancellation}
          policy={cancellationPolicy}
        />
      )}
      
      {/* Modal de reagendamento */}
      {reschedulePolicy && (
        <RescheduleModal
          appointment={appointment}
          userRole={userRole}
          isOpen={showRescheduleModal}
          onClose={() => setShowRescheduleModal(false)}
          onConfirm={handleConfirmReschedule}
          policy={reschedulePolicy}
          availableSlots={availableSlots}
          onFetchAvailableSlots={fetchAvailableSlots}
        />
      )}
    </div>
  );
};

export default AppointmentManagement;
